/**
 * trees.ts — The shape of a tree's canopy.
 *
 * Pure and unit-tested, and not just for tidiness: the whole tree-placement
 * guarantee rests on this. `TREES.CANOPY_RADIUS` is the envelope the clearance
 * test holds every tree to, and that promise is only worth anything if the
 * foliage actually fits inside it. So the layout is computed here, where
 * `treeClearance.test.ts` can assert containment directly, rather than being
 * scattered through the mesh-building code where nothing could check it.
 *
 * The same goes for how *high* the foliage reaches. At this camera tilt a
 * canopy is drawn well north of the trunk holding it, by an amount proportional
 * to its height — so the clearance test needs the real vertical extent, not a
 * guess. `canopyTop`/`canopyBottom` give it that from the actual blobs, which
 * means a new tree shape can't silently invalidate the placement checks.
 *
 * Each kind is a different way of stacking the same balls of clay:
 *
 *   round   a broad low ball with a couple of lobes pushed out around it
 *   tall    a narrow cone, blobs shrinking as they stack upward
 *   bushy   wide and many-lobed, sprawling further out than it goes up
 */
import { hash2 } from '../utils/math';
import type { Rect } from './level';
import type { TreeKind } from '../types';

/** One ball of clay in a canopy, positioned relative to the top of the trunk. */
export interface CanopyBlob {
  /** Offset from the trunk centre line, in world units. */
  x: number;
  y: number;
  z: number;
  radius: number;
}

/**
 * Per-kind layout. `spread` and `blobRadius` are fractions of the canopy
 * envelope and **must sum to at most 1** for every blob, or the foliage pokes
 * outside the envelope the clearance test relies on — `trees.test.ts` checks
 * this for all three kinds across a sweep of seeds, not just the ones in use.
 */
const SHAPES: Record<
  TreeKind,
  { blobs: number; spread: number; blobRadius: number; rise: number; taper: number }
> = {
  // Broad and low: one core ball, lobes pushed out sideways around it.
  round: { blobs: 3, spread: 0.42, blobRadius: 0.46, rise: 0.34, taper: 0 },
  // Narrow and stacked: little horizontal spread, each ball smaller than the
  // last, climbing well above the trunk into a column. The rise has to be large
  // relative to the trunk or the result is a lollipop on a stick rather than a
  // poplar — the crown needs to be the tall part, not the bare trunk under it.
  tall: { blobs: 5, spread: 0.2, blobRadius: 0.44, rise: 1.75, taper: 0.45 },
  // Sprawling: the most lobes, thrown widest, barely rising.
  bushy: { blobs: 5, spread: 0.5, blobRadius: 0.42, rise: 0.26, taper: 0.12 },
};

/** How many balls of clay a kind is built from. */
export function blobCount(kind: TreeKind): number {
  return SHAPES[kind].blobs;
}

/**
 * Build a canopy inside a sphere of `radius`, in the silhouette of `kind`.
 *
 * Jitter is derived from the tree's own position, so every tree differs from
 * its neighbours but is identical on every run (see `hash2`). Blob positions
 * are relative to the **top of the trunk**.
 *
 * @param seedX  the tree's world x
 * @param seedY  the tree's world y (gameplay y, i.e. world z)
 * @param radius the canopy envelope; every blob fits inside it horizontally
 */
export function canopyBlobs(
  seedX: number,
  seedY: number,
  radius: number,
  kind: TreeKind,
): CanopyBlob[] {
  const blobs: CanopyBlob[] = [];
  const s = SHAPES[kind];
  if (radius <= 0 || s.blobs <= 0) return blobs;

  // Core ball, sitting just above the trunk top.
  blobs.push({ x: 0, y: radius * s.rise * 0.25, z: 0, radius: radius * 0.62 });

  for (let i = 1; i < s.blobs; i++) {
    const t = (i - 1) / Math.max(s.blobs - 1, 1); // 0..1 up the tree
    // Spread the lobes around the trunk, nudged off an even split so they don't
    // form a visibly regular rosette.
    const spin = hash2(seedX + i * 7.3, seedY - i * 3.1);
    const angle = t * Math.PI * 2 + spin * 1.4;
    const reach = radius * s.spread * (0.7 + hash2(seedY + i * 5.9, seedX) * 0.3);
    // Tall trees climb as they go; round and bushy ones barely do.
    const lift = radius * s.rise * (0.35 + t * 0.65 + hash2(seedX - i * 2.7, seedY + i) * 0.2);
    blobs.push({
      x: Math.cos(angle) * reach,
      y: lift,
      z: Math.sin(angle) * reach,
      // Taper shrinks the higher lobes, which is what turns a stack into a cone.
      radius:
        radius * s.blobRadius * (1 - s.taper * t) * (0.85 + hash2(seedX + i, seedY + i) * 0.3),
    });
  }
  return blobs;
}

/**
 * Furthest any foliage reaches from the trunk on the ground plane. This is what
 * has to stay within `TREES.CANOPY_RADIUS` for the footway clearance to hold.
 */
export function canopyExtent(blobs: readonly CanopyBlob[]): number {
  let max = 0;
  for (const b of blobs) max = Math.max(max, Math.hypot(b.x, b.z) + b.radius);
  return max;
}

/**
 * Height of the highest foliage above the top of the trunk. Feeds the projected
 * clearance check — taller canopies are drawn further north of their trunks.
 */
export function canopyTop(blobs: readonly CanopyBlob[]): number {
  let max = 0;
  for (const b of blobs) max = Math.max(max, b.y + b.radius);
  return max;
}

/** Depth of the lowest foliage relative to the top of the trunk (usually negative). */
export function canopyBottom(blobs: readonly CanopyBlob[]): number {
  if (blobs.length === 0) return 0;
  let min = Infinity;
  for (const b of blobs) min = Math.min(min, b.y - b.radius);
  return min;
}

/**
 * The ground a tree's canopy covers, as a plain rect around its trunk.
 */
export function canopyRect(x: number, y: number, envelope: number): Rect {
  return { x: x - envelope, y: y - envelope, w: envelope * 2, h: envelope * 2 };
}

/**
 * Where a canopy actually *appears* on screen, which is not above its trunk.
 *
 * The camera is tilted `tiltDeg` off the horizontal, so anything at height h
 * renders about `h / tan(tilt)` toward the top of the screen — i.e. toward −y
 * in gameplay coordinates. At the game's 58° that's 0.63 units of drift per
 * unit of height, and a tree's foliage sits 40-odd units up, so a canopy is
 * drawn roughly 30 units *north* of the trunk holding it.
 *
 * That is the whole game for tree placement: a trunk comfortably south of a
 * footway can still have its foliage drawn on top of it, hiding the walker —
 * which a ground-plane check misses entirely. The rect returned is the smear
 * between where the canopy's lowest foliage lands and where its highest does.
 *
 * Measured off the real blobs rather than a fudge factor, so a new tree shape —
 * a taller one especially — can't quietly invalidate a clearance check that
 * depends on it. `treeClearance.test.ts` holds the authored town to this, and
 * `levelgen/validate.ts` holds every generated one.
 */
export function apparentCanopyRect(
  x: number,
  y: number,
  envelope: number,
  trunkTop: number,
  kind: TreeKind,
  tiltDeg: number,
): Rect {
  const blobs = canopyBlobs(x, y, envelope, kind);
  const perUnit = 1 / Math.tan((tiltDeg * Math.PI) / 180);
  const highest = (trunkTop + canopyTop(blobs)) * perUnit;
  const lowest = Math.max(trunkTop + canopyBottom(blobs), 0) * perUnit;
  return {
    x: x - envelope,
    y: y - envelope - highest,
    w: envelope * 2,
    h: envelope * 2 + (highest - lowest),
  };
}

/**
 * How much lighter or darker a blob sits than the tree's own leaf colour,
 * from its height within the canopy.
 *
 * The reason is the same one that makes every character in the game two-tone:
 * **from directly above, a single-colour rounded mass is a featureless lump.**
 * A canopy is exactly that — a pile of balls in one green — and this camera
 * spends most of its time looking straight down onto it. Shading by height puts
 * the sunlit crown against the shaded underskirt and gives the foliage a
 * top and a bottom again.
 *
 * `y` is the blob's centre relative to the top of the trunk, measured against
 * the canopy's own extent so a squat bushy tree gets the same tonal spread as a
 * tall one rather than a fraction of it.
 */
export function blobShade(y: number, bottom: number, top: number, spread: number): number {
  const range = top - bottom;
  if (!(range > 0)) return 0;
  const t = Math.min(Math.max((y - bottom) / range, 0), 1);
  // −spread at the very bottom of the canopy, +spread at the very top.
  return (t * 2 - 1) * spread;
}
